import React, { useState } from 'react';
import { Send, Calendar, MessageSquare, FileText, Users } from 'lucide-react';
import AdminSidebarNavigation from 'components/ui/AdminSidebarNavigation';
import RecipientSelector from './components/RecipientSelector';
import MessageComposer from './components/MessageComposer';
import TemplateSelector from './components/TemplateSelector';
import TemplateManagement from './components/TemplateManagement';
import CampaignManager from './components/CampaignManager';
import DeliveryTracking from './components/DeliveryTracking';
import MessageHistory from './components/MessageHistory';
import { communicationService } from '../../services/communicationService';

const AdminCommunicationCenter = () => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [messageContent, setMessageContent] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('whatsapp');
  const [scheduledTime, setScheduledTime] = useState(null);
  const [activeTab, setActiveTab] = useState('compose');
  const [recipientCriteria, setRecipientCriteria] = useState(null);
  const [isSending, setIsSending] = useState(false);

  const handleSelectTemplate = (template) => {
    setActiveTemplate(template);
    // If static message, set content directly
    if (template.is_static_message) {
      setMessageContent(template.template_message);
    } else if (template.is_runtime_message) {
      // For runtime messages, start with empty content (user will type)
      setMessageContent('');
    } else {
      // For templated messages, content will be rendered when variables are filled
      setMessageContent('');
    }
  };

  const handleTemplateChange = (template) => {
    setActiveTemplate(template);
  };

  const handleSendMessage = async () => {
    if (selectedRecipients.length === 0) {
      alert('Please select at least one recipient');
      return;
    }

    if (!messageContent.trim()) {
      alert('Please enter a message');
      return;
    }

    if (!activeTemplate) {
      alert('Please select a template');
      return;
    }

    setIsSending(true);
    try {
      // Prepare variables for each recipient and render messages
      const results = [];
      const executionRecords = [];
      
      for (const recipient of selectedRecipients) {
        try {
          const variables = {
            name: recipient.name || 'Coach',
            school: recipient.school || 'School',
            city: recipient.city || '',
            category: recipient.category || ''
          };

          // Render the message with variables (or use runtime message)
          const renderedMessage = activeTemplate.is_runtime_message 
            ? messageContent 
            : await communicationService.renderTemplate(activeTemplate, variables, messageContent);

          // Send message
          const sendResult = await communicationService.sendQuickMessage(
            activeTemplate.id,
            [recipient],
            variables
          );
          
          const result = sendResult[0]; // sendQuickMessage returns array
          results.push({ recipient, status: result.status, providerMessageId: result.providerMessageId });
          
          // Prepare execution record
          executionRecords.push({
            templateId: activeTemplate.id,
            registrationId: recipient.id,
            recipientMobile: recipient.mobile,
            recipientEmail: recipient.email,
            messageContent: renderedMessage,
            messageType: activeTemplate.message_type,
            status: result.status === 'sent' ? 'SENT' : 'FAILED',
            providerMessageId: result.providerMessageId,
            errorMessage: result.error || null,
            sentAt: result.status === 'sent' ? new Date().toISOString() : null
          });
        } catch (error) {
          console.error(`Failed to send to ${recipient.mobile}:`, error);
          results.push({ recipient, status: 'failed', error: error.message });
          
          // Render message even for failed sends (for record keeping)
          let renderedMessage = messageContent;
          try {
            if (activeTemplate.is_runtime_message) {
              renderedMessage = messageContent;
            } else {
              const variables = {
                name: recipient.name || 'Coach',
                school: recipient.school || 'School',
                city: recipient.city || '',
                category: recipient.category || ''
              };
              renderedMessage = await communicationService.renderTemplate(activeTemplate, variables, messageContent);
            }
          } catch (renderError) {
            console.error('Error rendering template:', renderError);
          }
          
          // Prepare execution record for failed send
          executionRecords.push({
            templateId: activeTemplate.id,
            registrationId: recipient.id,
            recipientMobile: recipient.mobile,
            recipientEmail: recipient.email,
            messageContent: renderedMessage,
            messageType: activeTemplate.message_type,
            status: 'FAILED',
            errorMessage: error.message,
            sentAt: null
          });
        }
      }

      const sentCount = results.filter(r => r.status === 'sent').length;
      const failedCount = results.filter(r => r.status === 'failed').length;

      // Create campaign record for tracking
      const campaignName = `Quick Send - ${new Date().toLocaleString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      })}`;
      
      // Derive criteria from selected recipients
      const cities = [...new Set(selectedRecipients.map(r => r.city).filter(Boolean))];
      const statuses = [...new Set(selectedRecipients.map(r => r.status).filter(Boolean))];
      const ageCategories = [...new Set(selectedRecipients.map(r => {
        // Map display category to database format
        const map = { 'Boys U14': 'boys_u14', 'Girls U15': 'girls_u15', 'Boys U16': 'boys_u16' };
        return map[r.category] || r.category;
      }).filter(Boolean))];

      const campaign = await communicationService.createCampaign({
        name: campaignName,
        templateIds: [activeTemplate.id],
        campaignType: 'DYNAMIC',
        description: `Quick send to ${selectedRecipients.length} recipient(s)`,
        recipientCriteria: {
          city: cities.length === 1 ? cities[0] : null,
          status: statuses.length > 0 ? statuses : [],
          ageCategory: ageCategories.length > 0 ? ageCategories : [],
          recipientType: ['POC'] // Default to POC for quick sends (backward compatible)
        },
        scheduledAt: scheduledTime ? new Date(scheduledTime).toISOString() : null,
        runtimeMessage: activeTemplate.is_runtime_message ? messageContent : null
      });

      // Update campaign with execution results
      await communicationService.updateCampaign(campaign.id, {
        status: 'COMPLETED',
        total_recipients: selectedRecipients.length,
        sent_count: sentCount,
        failed_count: failedCount,
        executed_at: new Date().toISOString()
      });

      // Create execution records with proper status
      for (const execution of executionRecords) {
        try {
          const createdExecution = await communicationService.createExecution({
            campaignId: campaign.id,
            templateId: execution.templateId,
            registrationId: execution.registrationId,
            recipientMobile: execution.recipientMobile,
            recipientEmail: execution.recipientEmail,
            messageContent: execution.messageContent,
            messageType: execution.messageType
          });
          
          // Update execution status immediately
          if (execution.status === 'SENT' && execution.sentAt) {
            await communicationService.updateExecution(createdExecution.id, {
              status: 'SENT',
              sent_at: execution.sentAt,
              provider_message_id: execution.providerMessageId
            });
          } else if (execution.status === 'FAILED') {
            await communicationService.updateExecution(createdExecution.id, {
              status: 'FAILED',
              error_message: execution.errorMessage
            });
          }
        } catch (error) {
          console.error('Error creating execution record:', error);
        }
      }

      alert(`Message ${scheduledTime ? 'scheduled' : 'sent'} successfully!\nSent: ${sentCount}, Failed: ${failedCount}`);

      // Reset form
      setSelectedRecipients([]);
      setMessageContent('');
      setActiveTemplate(null);
      setScheduledTime(null);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('Failed to send message: ' + error.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleBulkSelect = (data) => {
    if (data.recipients) {
      setSelectedRecipients(prev => {
        const existingIds = new Set(prev.map(r => r.id));
        const newRecipients = data.recipients.filter(r => !existingIds.has(r.id));
        return [...prev, ...newRecipients];
      });
    }
  };

  const handleCriteriaChange = (criteria) => {
    setRecipientCriteria(criteria);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminSidebarNavigation 
        isCollapsed={isSidebarCollapsed}
        onToggle={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <div className={`main-content with-sidebar ${isSidebarCollapsed ? 'collapsed' : ''} flex flex-col overflow-hidden`}>
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Communication Center</h1>
              <p className="text-xs sm:text-sm text-gray-600 mt-1">Send messages to coaches and teams</p>
            </div>
            {activeTab === 'compose' && (
              <div className="flex items-center gap-2 sm:gap-3">
                <button 
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  disabled={!scheduledTime}
                >
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Schedule</span>
                </button>
                <button 
                  onClick={handleSendMessage}
                  disabled={selectedRecipients?.length === 0 || !messageContent || !activeTemplate || isSending}
                  className="flex items-center gap-2 px-4 sm:px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base touch-target"
                >
                  <Send className="w-4 h-4" />
                  <span className="hidden sm:inline">{isSending ? 'Sending...' : 'Send Message'}</span>
                  <span className="sm:hidden">{isSending ? 'Sending...' : 'Send'}</span>
                </button>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex overflow-x-auto whitespace-nowrap mt-4 border-b border-gray-200 -mb-px">
            <button
              onClick={() => setActiveTab('compose')}
              className={`pb-2 sm:pb-3 px-2 sm:px-3 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap touch-target ${
                activeTab === 'compose'
                  ? 'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Compose</span>
              <span className="sm:hidden">Compose</span>
            </button>
            <button
              onClick={() => setActiveTab('templates')}
              className={`pb-2 sm:pb-3 px-2 sm:px-3 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap touch-target ${
                activeTab === 'templates'
                  ? 'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Message Templates</span>
              <span className="sm:hidden">Templates</span>
            </button>
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`pb-2 sm:pb-3 px-2 sm:px-3 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap touch-target ${
                activeTab === 'campaigns'
                  ? 'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Campaigns</span>
              <span className="sm:hidden">Campaigns</span>
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`pb-2 sm:pb-3 px-2 sm:px-3 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap touch-target ${
                activeTab === 'history'
                  ? 'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Message History</span>
              <span className="sm:hidden">History</span>
            </button>
            <button
              onClick={() => setActiveTab('tracking')}
              className={`pb-2 sm:pb-3 px-2 sm:px-3 font-medium text-xs sm:text-sm transition-colors whitespace-nowrap touch-target ${
                activeTab === 'tracking'
                  ? 'text-blue-600 border-b-2 border-blue-600' :'text-gray-600 hover:text-gray-900'
              }`}
            >
              <span className="hidden sm:inline">Delivery Tracking</span>
              <span className="sm:hidden">Tracking</span>
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-auto">
          {activeTab === 'compose' && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6 p-4 sm:p-6 h-full">
              {/* Left Panel - Recipient Selection */}
              <div className="lg:col-span-4 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col">
                <RecipientSelector
                  selectedRecipients={selectedRecipients}
                  onSelectRecipients={setSelectedRecipients}
                  onBulkSelect={handleBulkSelect}
                  onCriteriaChange={handleCriteriaChange}
                />
              </div>

              {/* Right Panel - Message Composition */}
              <div className="lg:col-span-8 space-y-4">
                <TemplateSelector
                  selectedTemplate={activeTemplate}
                  onSelectTemplate={handleSelectTemplate}
                  onNavigateToTemplates={() => setActiveTab('templates')}
                />
                
                <MessageComposer
                  content={messageContent}
                  onContentChange={setMessageContent}
                  deliveryMethod={deliveryMethod}
                  onDeliveryMethodChange={setDeliveryMethod}
                  scheduledTime={scheduledTime}
                  onScheduledTimeChange={setScheduledTime}
                  recipientCount={selectedRecipients?.length}
                  selectedTemplate={activeTemplate}
                  onTemplateChange={handleTemplateChange}
                />
              </div>
            </div>
          )}

          {activeTab === 'templates' && (
            <div className="p-4 sm:p-6">
              <TemplateManagement />
            </div>
          )}

          {activeTab === 'campaigns' && (
            <div className="p-4 sm:p-6">
              <CampaignManager />
            </div>
          )}

          {activeTab === 'history' && (
            <div className="p-4 sm:p-6">
              <MessageHistory />
            </div>
          )}

          {activeTab === 'tracking' && (
            <div className="p-4 sm:p-6">
              <DeliveryTracking />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCommunicationCenter;
